/*
=========================================================
* Promo Announcement Banner
=========================================================
*
* Prominent top-of-page strip advertising the active limited-time promo.
* Renders nothing outside the promo window (constants/promo.js), so it
* self-expires without a redeploy. Dismissal is remembered per-promo in
* localStorage (keyed by promo name + end date, so a future promo shows
* the banner again).
*/

import { useState } from "react";

import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import useSignupUrl from "hooks/useSignupUrl";
import { isPromoActive, PROMO_TRIAL } from "constants/promo";

const DISMISS_KEY = `promo-banner-dismissed:${PROMO_TRIAL.name}:${PROMO_TRIAL.signupEnd}`;

function wasDismissed() {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function PromoBanner() {
  const signupUrl = useSignupUrl();
  const [dismissed, setDismissed] = useState(wasDismissed);

  if (!isPromoActive() || dismissed) return null;

  const months = Math.round(PROMO_TRIAL.trialDays / 30);
  const endsLabel = new Date(`${PROMO_TRIAL.signupEnd}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // localStorage unavailable (private browsing) — banner reappears next visit
    }
  };

  return (
    <MKBox
      sx={{
        background: "linear-gradient(90deg, #14130F 0%, #2B2416 55%, #14130F 100%)",
        position: "relative",
        zIndex: 11,
        borderBottom: "2px solid #FFD37E",
      }}
    >
      <Container>
        <MKBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          columnGap={{ xs: 1.5, md: 3 }}
          rowGap={1}
          py={{ xs: 1.75, md: 2.25 }}
          pr={{ xs: 5, sm: 7 }}
          pl={{ xs: 1, sm: 0 }}
        >
          <MKBox sx={{ textAlign: { xs: "center", md: "left" } }}>
            <MKTypography
              component="span"
              sx={{
                display: "block",
                color: "#FFD37E",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", md: "0.8rem" },
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                lineHeight: 1.3,
              }}
            >
              ☀️ {PROMO_TRIAL.name} · Limited Time
            </MKTypography>
            <MKTypography
              component="span"
              sx={{
                display: "block",
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: "1.05rem", md: "1.3rem" },
                lineHeight: 1.35,
              }}
            >
              {months} months free on all plans — offer ends {endsLabel}
            </MKTypography>
          </MKBox>
          <MKButton
            component="a"
            href={signupUrl}
            size="medium"
            sx={{
              background: "#FFD37E",
              color: "#14130F",
              fontWeight: 700,
              px: 3,
              whiteSpace: "nowrap",
              "&:hover": { background: "#fff", color: "#14130F" },
              "&:focus:not(:hover)": { background: "#FFD37E", color: "#14130F" },
            }}
          >
            Start Free Trial →
          </MKButton>
          <IconButton
            size="small"
            aria-label="Dismiss promotion"
            onClick={dismiss}
            sx={{
              color: "rgba(255,255,255,0.7)",
              position: "absolute",
              right: { xs: 4, sm: 12 },
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </MKBox>
      </Container>
    </MKBox>
  );
}

export default PromoBanner;
